import { openIndexedDb, requestToPromise, transactionDone } from '../utils/idb';
import {
  canUseNativeArchiveCheckpointStore,
  nativeArchiveCheckpointStore
} from '../utils/webviewBridge';

const DB_NAME = 'family-messenger-message-archive';
const DB_VERSION = 2;
const CHECKPOINTS = 'writer_checkpoints';
const CACHE = 'archive_cache';

export type MessageArchiveWriterCheckpoint = {
  checkpointId: string;
  sourceCircleIdentityId: string;
  destinationId: string;
  archiveId: string;
  writerDeviceId: string;
  currentPeriodKey: string | null;
  pendingPeriodKey?: string | null;
  lastKnownManifestRevision: number | null;
  lastRunAt: string | null;
  updatedAt: string;
};

export function buildMessageArchiveCheckpointId(params: {
  sourceCircleIdentityId: string;
  destinationId: string;
}): string {
  return `${params.sourceCircleIdentityId.trim()}::${params.destinationId.trim()}`;
}

function openDb(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(CHECKPOINTS)) {
      const store = db.createObjectStore(CHECKPOINTS, { keyPath: 'checkpointId' });
      store.createIndex('bySourceCircle', 'sourceCircleIdentityId');
      store.createIndex('byArchiveId', 'archiveId');
    }
    if (!db.objectStoreNames.contains(CACHE)) {
      const store = db.createObjectStore(CACHE, { keyPath: 'cacheId' });
      store.createIndex('byArchive', ['sourceCircleIdentityId', 'archiveId']);
      store.createIndex('byConversation', ['archiveId', 'conversationId']);
    }
  });
}

export async function getMessageArchiveWriterCheckpoint(
  checkpointId: string
): Promise<MessageArchiveWriterCheckpoint | null> {
  if (canUseNativeArchiveCheckpointStore()) {
    const raw = nativeArchiveCheckpointStore.get(checkpointId);
    if (raw) {
      try {
        return JSON.parse(raw) as MessageArchiveWriterCheckpoint;
      } catch {
        // Fall back to IndexedDB.
      }
    }
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CHECKPOINTS, 'readonly');
    const row = await requestToPromise(tx.objectStore(CHECKPOINTS).get(checkpointId));
    return (row || null) as MessageArchiveWriterCheckpoint | null;
  } finally {
    db.close();
  }
}

export async function saveMessageArchiveWriterCheckpoint(
  checkpoint: MessageArchiveWriterCheckpoint
): Promise<void> {
  const next = {
    ...checkpoint,
    updatedAt: new Date().toISOString()
  };

  if (canUseNativeArchiveCheckpointStore() && nativeArchiveCheckpointStore.save(next.checkpointId, JSON.stringify(next))) {
    return;
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CHECKPOINTS, 'readwrite');
    tx.objectStore(CHECKPOINTS).put(next);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function deleteMessageArchiveWriterCheckpoint(checkpointId: string): Promise<void> {
  if (canUseNativeArchiveCheckpointStore()) {
    nativeArchiveCheckpointStore.delete(checkpointId);
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CHECKPOINTS, 'readwrite');
    tx.objectStore(CHECKPOINTS).delete(checkpointId);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function deleteMessageArchiveWriterCheckpointsForIdentity(sourceCircleIdentityId: string): Promise<void> {
  if (canUseNativeArchiveCheckpointStore()) {
    nativeArchiveCheckpointStore.deleteForIdentity(sourceCircleIdentityId);
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CHECKPOINTS, 'readwrite');
    const index = tx.objectStore(CHECKPOINTS).index('bySourceCircle');
    const rows = await requestToPromise(index.getAllKeys(sourceCircleIdentityId)) as IDBValidKey[];
    for (const key of rows) tx.objectStore(CHECKPOINTS).delete(key);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}
